import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Building2, Search, Eye, CheckCircle, XCircle, 
  Clock, Trash2, Phone, ExternalLink, Users, Briefcase, FolderOpen, MessageSquare, Settings, Mail,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminVendorsPage = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }
    fetchApplications();
  }, [navigate]);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vendors`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, notes = null) => {
    try {
      await axios.put(`${BACKEND_URL}/api/vendors/${id}/status`, { status, notes });
      fetchApplications();
      if (selectedApplication?.id === id) {
        setSelectedApplication({ ...selectedApplication, status, notes });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/vendors/${id}`);
      fetchApplications();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  const filteredApplications = useMemo(() => {
    let result = applications.filter(app => {
      const matchesSearch = 
        app.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.trade_type.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    
    // Sort the results
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle different field types
      if (sortField === 'created_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (sortField === 'years_in_business') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [applications, searchTerm, statusFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-slate-300" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-cyan-600" /> 
      : <ArrowDown className="w-4 h-4 text-cyan-600" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewed: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const statusCounts = {
    all: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };

  return (
    <AdminSidebar pageTitle="Vendor Applications" pageIcon={Briefcase}>
      {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {Object.entries(statusCounts).map(([status, count]) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`p-4 rounded-xl text-center transition-all ${
                  statusFilter === status 
                    ? 'bg-cyan-600 text-white shadow-lg' 
                    : 'bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-sm capitalize">{status}</p>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by company, contact, or trade..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

      {/* Applications List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-slate-600 mt-4">Loading applications...</p>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No vendor applications found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">
                    <button onClick={() => handleSort('company_name')} className="flex items-center gap-1 hover:text-cyan-600">
                      Company <SortIcon field="company_name" />
                    </button>
                  </th>
                  <th className="text-left p-4 font-semibold text-slate-700">
                    <button onClick={() => handleSort('contact_name')} className="flex items-center gap-1 hover:text-cyan-600">
                      Contact <SortIcon field="contact_name" />
                    </button>
                  </th>
                  <th className="text-left p-4 font-semibold text-slate-700">
                    <button onClick={() => handleSort('trade_type')} className="flex items-center gap-1 hover:text-cyan-600">
                      Trade <SortIcon field="trade_type" />
                    </button>
                  </th>
                  <th className="text-left p-4 font-semibold text-slate-700">
                    <button onClick={() => handleSort('status')} className="flex items-center gap-1 hover:text-cyan-600">
                      Status <SortIcon field="status" />
                    </button>
                  </th>
                  <th className="text-left p-4 font-semibold text-slate-700">
                    <button onClick={() => handleSort('created_at')} className="flex items-center gap-1 hover:text-cyan-600">
                      Date <SortIcon field="created_at" />
                    </button>
                  </th>
                  <th className="text-left p-4 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map((app) => (
                  <tr key={app.id} className="border-b hover:bg-slate-50">
                    <td className="p-4">
                      <p className="font-medium text-slate-900">{app.company_name}</p>
                      <p className="text-sm text-slate-500">{app.service_area}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-slate-900">{app.contact_name}</p>
                      <p className="text-sm text-slate-500">{app.email}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-slate-700">{app.trade_type}</p>
                      <p className="text-sm text-slate-500">{app.years_in_business} years</p>
                    </td>
                    <td className="p-4">{getStatusBadge(app.status)}</td>
                    <td className="p-4 text-sm text-slate-600">
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedApplication(app); setShowDetailModal(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteApplication(app.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedApplication && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">{selectedApplication.company_name}</h2>
                <Button variant="ghost" onClick={() => setShowDetailModal(false)}>×</Button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Contact Name</p>
                  <p className="font-medium">{selectedApplication.contact_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Trade Type</p>
                  <p className="font-medium">{selectedApplication.trade_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <a href={`mailto:${selectedApplication.email}`} className="font-medium text-cyan-600 flex items-center gap-1">
                    {selectedApplication.email} <Mail className="w-3 h-3" />
                  </a>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <a href={`tel:${selectedApplication.phone}`} className="font-medium text-cyan-600 flex items-center gap-1">
                    {selectedApplication.phone} <Phone className="w-3 h-3" />
                  </a>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Years in Business</p>
                  <p className="font-medium">{selectedApplication.years_in_business}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Service Area</p>
                  <p className="font-medium">{selectedApplication.service_area}</p>
                </div>
                {selectedApplication.website && (
                  <div>
                    <p className="text-sm text-slate-500">Website</p>
                    <a href={selectedApplication.website} target="_blank" rel="noopener noreferrer" className="font-medium text-cyan-600 flex items-center gap-1">
                      Visit Site <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {selectedApplication.license_number && (
                  <div>
                    <p className="text-sm text-slate-500">License Number</p>
                    <p className="font-medium">{selectedApplication.license_number}</p>
                  </div>
                )}
              </div>
              
              {/* Description */}
              <div>
                <p className="text-sm text-slate-500 mb-2">Company Description</p>
                <p className="text-slate-700 bg-slate-50 p-4 rounded-lg">{selectedApplication.description}</p>
              </div>

              {/* Status Update */}
              <div className="border-t pt-6">
                <p className="text-sm text-slate-500 mb-3">Update Status</p>
                <div className="flex flex-wrap gap-2">
                  {['pending', 'reviewed', 'approved', 'rejected'].map(status => (
                    <Button
                      key={status}
                      variant={selectedApplication.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => updateStatus(selectedApplication.id, status)}
                      className={selectedApplication.status === status ? 'bg-cyan-600' : ''}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </AdminSidebar>
  );
};

export default AdminVendorsPage;
